import React, { useState, useEffect } from 'react';
import { staffApi, branchApi, formatApiErrorDetail, userApi } from '../../lib/api';
import { formatDate, getStatusColor, getRoleLabel } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Users,
  Plus,
  MoreHorizontal,
  Edit2,
  Loader2,
  Search,
  Eye,
  EyeOff,
  DollarSign,
  Trash2,
  KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/utils';

export default function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [branchFilter, setBranchFilter] = useState('all');
  const [showPassword, setShowPassword] = useState(false);
  const [showCommissionDialog, setShowCommissionDialog] = useState(false);
  const [commissionStaff, setCommissionStaff] = useState(null);
  const [commissionTiers, setCommissionTiers] = useState([]);
  const [savingCommission, setSavingCommission] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'SALES_STAFF',
    branch_id: '',
    incentive_target: 0,
  });

  const fetchData = async () => {
    try {
      const [staffRes, branchesRes] = await Promise.all([
        staffApi.list(),
        branchApi.list(),
      ]);
      const sData = staffRes.data;
      setStaff(sData.items || sData);
      const bData = branchesRes.data;
      setBranches((bData.items || bData).filter(b => b.status === 'ACTIVE'));
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'SALES_STAFF',
      branch_id: '',
      incentive_target: 0,
    });
    setEditingStaff(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleOpenEdit = (member) => {
    setEditingStaff(member);
    setFormData({
      name: member.name,
      email: member.email,
      password: '',
      role: member.role,
      branch_id: member.branch_id || '',
      incentive_target: member.incentive_target || 0,
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingStaff) {
        const updateData = {
          name: formData.name,
          role: formData.role,
          branch_id: formData.branch_id || null,
          incentive_target: formData.incentive_target,
        };
        await staffApi.update(editingStaff.id, updateData);
        toast.success('Staff updated');
      } else {
        if (!formData.password || formData.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          setSaving(false);
          return;
        }
        await staffApi.create({
          ...formData,
          branch_id: formData.branch_id || null,
        });
        toast.success('Staff created');
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to save staff');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (member) => {
    try {
      const newStatus = member.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await staffApi.update(member.id, { status: newStatus });
      toast.success(`Staff ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update status');
    }
  };

  const getBranchName = (branchId) => {
    if (!branchId) return 'Not assigned';
    const branch = branches.find(b => b.id === branchId);
    return branch?.branch_name || 'Unknown';
  };

  const handleOpenCommission = async (member) => {
    setCommissionStaff(member);
    try {
      const { data } = await staffApi.getCommissionTiers(member.id);
      setCommissionTiers(data.commission_tiers?.length > 0 ? data.commission_tiers : [{ min_sales: 0, max_sales: 10, commission_per_sale: 50 }]);
    } catch {
      setCommissionTiers([{ min_sales: 0, max_sales: 10, commission_per_sale: 50 }]);
    }
    setShowCommissionDialog(true);
  };

  const addTier = () => {
    const last = commissionTiers[commissionTiers.length - 1];
    const nextMin = last ? (last.max_sales === -1 ? last.min_sales + 10 : last.max_sales + 1) : 0;
    setCommissionTiers([...commissionTiers, { min_sales: nextMin, max_sales: -1, commission_per_sale: 0 }]);
  };

  const removeTier = (idx) => {
    setCommissionTiers(commissionTiers.filter((_, i) => i !== idx));
  };

  const updateTier = (idx, field, value) => {
    const updated = [...commissionTiers];
    updated[idx] = { ...updated[idx], [field]: value };
    setCommissionTiers(updated);
  };

  const handleSaveCommission = async () => {
    setSavingCommission(true);
    try {
      await staffApi.updateCommissionTiers(commissionStaff.id, { commission_tiers: commissionTiers });
      toast.success('Commission tiers saved');
      setShowCommissionDialog(false);
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to save commission tiers');
    } finally {
      setSavingCommission(false);
    }
  };

  const filteredStaff = staff.filter((member) => {
    if (branchFilter !== 'all' && member.branch_id !== branchFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        member.name.toLowerCase().includes(query) ||
        member.email.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Staff
            </h1>
            <p className="text-slate-600 mt-1">Manage your team members</p>
          </div>
          <Button
            onClick={handleOpenCreate}
            className="bg-[#005DE8] hover:bg-[#0047B3]"
            data-testid="create-staff-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Staff
          </Button>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search staff..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="search-staff"
                  />
                </div>
              </div>
              <Select value={branchFilter} onValueChange={setBranchFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="branch-filter">
                  <SelectValue placeholder="Branch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.branch_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Staff Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" />
              </div>
            ) : filteredStaff.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStaff.map((member) => (
                    <TableRow key={member.id} className="table-row-hover">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-[#005DE8] flex items-center justify-center text-white font-medium text-sm">
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-slate-900">{member.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{member.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-slate-200">
                          {getRoleLabel(member.role)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {getBranchName(member.branch_id)}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {member.incentive_target || 0} units/month
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusColor(member.status)} border`}>
                          {member.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`staff-actions-${member.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenEdit(member)}>
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleOpenCommission(member)}>
                              <DollarSign className="h-4 w-4 mr-2" />
                              Commission Tiers
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleStatus(member)}>
                              {member.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={async () => {
                              try {
                                const { data } = await userApi.resendCredentials(member.id);
                                toast.success(`Password reset for ${member.email}. New password: ${data.temp_password}`);
                              } catch (err) {
                                toast.error('Failed to reset password');
                              }
                            }}>
                              <KeyRound className="h-4 w-4 mr-2" />
                              Reset Password
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-16">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No staff found</h3>
                <p className="text-slate-500 mt-1">Get started by adding your first staff member</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>
                {editingStaff ? 'Edit Staff' : 'Add New Staff'}
              </DialogTitle>
              <DialogDescription>
                {editingStaff ? 'Update staff details' : 'Create a new team member account'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    required
                    data-testid="staff-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    required
                    disabled={!!editingStaff}
                    data-testid="staff-email-input"
                  />
                </div>

                {!editingStaff && (
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Minimum 6 characters"
                        required
                        minLength={6}
                        data-testid="staff-password-input"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select
                      value={formData.role}
                      onValueChange={(value) => setFormData({ ...formData, role: value })}
                    >
                      <SelectTrigger data-testid="staff-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="STORE_MANAGER">Store Manager</SelectItem>
                        <SelectItem value="SALES_STAFF">Sales Staff</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="branch">Branch</Label>
                    <Select
                      value={formData.branch_id || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, branch_id: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger data-testid="staff-branch-select">
                        <SelectValue placeholder="Select branch" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not assigned</SelectItem>
                        {branches.map((branch) => (
                          <SelectItem key={branch.id} value={branch.id}>
                            {branch.branch_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="incentive_target">Monthly Incentive Target (Warranty Sales)</Label>
                  <Input
                    id="incentive_target"
                    type="number"
                    min="0"
                    value={formData.incentive_target}
                    onChange={(e) => setFormData({ ...formData, incentive_target: parseInt(e.target.value) || 0 })}
                    data-testid="staff-target-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#005DE8] hover:bg-[#0047B3]"
                  disabled={saving}
                  data-testid="staff-submit-button"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  {editingStaff ? 'Update Staff' : 'Create Staff'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Commission Tiers Dialog */}
        <Dialog open={showCommissionDialog} onOpenChange={setShowCommissionDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>
                Commission Tiers - {commissionStaff?.name}
              </DialogTitle>
              <DialogDescription>
                Define incremental commission rates based on warranty sales volume
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {commissionTiers.map((tier, idx) => (
                <div key={idx} className="flex items-end gap-2 p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500">From (sales)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={tier.min_sales}
                      onChange={(e) => updateTier(idx, 'min_sales', parseInt(e.target.value) || 0)}
                      className="h-9"
                      data-testid={`tier-min-${idx}`}
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500">To (sales, -1=no limit)</Label>
                    <Input
                      type="number"
                      min="-1"
                      value={tier.max_sales}
                      onChange={(e) => updateTier(idx, 'max_sales', parseInt(e.target.value) || 0)}
                      className="h-9"
                      data-testid={`tier-max-${idx}`}
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500">Per sale (INR)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={tier.commission_per_sale}
                      onChange={(e) => updateTier(idx, 'commission_per_sale', parseFloat(e.target.value) || 0)}
                      className="h-9"
                      data-testid={`tier-rate-${idx}`}
                    />
                  </div>
                  {commissionTiers.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTier(idx)}
                      className="text-red-500 hover:text-red-700 h-9 w-9 p-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addTier} className="w-full" data-testid="add-tier-btn">
                <Plus className="h-4 w-4 mr-2" />Add Tier
              </Button>
              {commissionTiers.length > 0 && (
                <div className="text-xs text-slate-500 bg-blue-50 p-3 rounded-lg">
                  <strong>Example:</strong> If tiers are 0-10 = {formatCurrency(50)}/sale, 11-20 = {formatCurrency(75)}/sale, then a staff with 15 warranty sales earns: 11 x {formatCurrency(50)} + 5 x {formatCurrency(75)} = {formatCurrency(925)}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCommissionDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveCommission} className="bg-[#005DE8] hover:bg-[#0047B3]" disabled={savingCommission} data-testid="save-commission-btn">
                {savingCommission ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Save Tiers
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
